import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Popconfirm, Segmented, Space, Switch, Tag, Typography } from 'antd';
import type { TableColumnType } from 'antd';
import { FileText, Inbox, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { DataTable } from '@shared/components/DataTable';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { useActiveLanguages } from '@features/languages/useLanguages';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { formsApi } from '@api/forms';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { FORM_TYPES, type FormTypeValue } from '../constants';
import type { FormCreateInput, FormListItem } from '../types';
import { FormCreateModal } from '../components/FormCreateModal';

const { Text } = Typography;
type TypeFilter = 'ALL' | FormTypeValue;

export function FormsPage() {
  const { t } = useTranslation(['forms', 'common']);
  const td = t as unknown as (key: string) => string;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.FORM_MANAGE);
  const canSubmissions = useCan(PERMISSIONS.FORM_SUBMISSIONS);

  const [languageId, setLanguageId] = useState<string | undefined>(undefined);
  const [formType, setFormType] = useState<TypeFilter>('ALL');
  const [modalOpen, setModalOpen] = useState(false);

  const { data: languages = [] } = useActiveLanguages();
  const langCodeById = useMemo(() => new Map(languages.map((l) => [l.id, l.code])), [languages]);

  const { data: forms = [], isLoading } = useQuery({
    queryKey: ['forms', { languageId, formType }],
    queryFn: () =>
      formsApi.list({ languageId, formType: formType === 'ALL' ? undefined : formType }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['forms'] });

  const createMutation = useMutation({
    mutationFn: (input: FormCreateInput) => formsApi.create(input),
    onSuccess: async (created) => {
      toast.success(t('toast.created'));
      setModalOpen(false);
      await invalidate();
      void navigate(ROUTES.FORM_EDIT.replace(':id', created.id));
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: (row: FormListItem) => formsApi.setPublished(row.id, row.status !== 'Published'),
    onSuccess: (_data, row) => {
      toast.success(row.status === 'Published' ? t('toast.unpublished') : t('toast.published'));
      return invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => formsApi.remove(id),
    onSuccess: async () => {
      toast.success(t('toast.deleted'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const goEdit = (id: string) => void navigate(ROUTES.FORM_EDIT.replace(':id', id));
  const goSubmissions = (id: string) => void navigate(ROUTES.FORM_SUBMISSIONS.replace(':id', id));

  const columns: TableColumnType<FormListItem>[] = [
    {
      title: t('list.columns.title'),
      dataIndex: 'title',
      key: 'title',
      render: (title: string, row) => (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => goEdit(row.id)}>
          {title || t('editor.notFound')}
        </Button>
      ),
    },
    {
      title: t('list.columns.type'),
      dataIndex: 'formType',
      key: 'formType',
      width: 120,
      render: (type: FormTypeValue) => <Tag>{td(`type.${type}`)}</Tag>,
    },
    {
      title: t('list.columns.language'),
      key: 'language',
      width: 90,
      render: (_, r) =>
        r.languageId ? (
          <Tag color="blue">{langCodeById.get(r.languageId) ?? r.languageId.slice(0, 8)}</Tag>
        ) : null,
    },
    {
      title: t('list.columns.status'),
      key: 'status',
      width: 150,
      render: (_, r) => (
        <Space size={8}>
          <Switch
            size="small"
            checked={r.status === 'Published'}
            disabled={!canManage || publishMutation.isPending}
            onChange={() => publishMutation.mutate(r)}
            aria-label={t('list.columns.status')}
          />
          <Tag color={r.status === 'Published' ? 'green' : 'default'}>
            {r.status === 'Published' ? t('status.Published') : t('status.Draft')}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('list.columns.submissions'),
      dataIndex: 'submissionCount',
      key: 'submissionCount',
      width: 110,
      align: 'right',
      render: (count: number, r) =>
        canSubmissions ? (
          <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => goSubmissions(r.id)}>
            {count}
          </Button>
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>
        ),
    },
    {
      title: t('list.columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      align: 'right',
      width: 130,
      render: (_, r) => (
        <Space size={4}>
          {canSubmissions ? (
            <Button
              type="text"
              size="small"
              icon={<Inbox size={15} />}
              onClick={() => goSubmissions(r.id)}
              aria-label={t('list.openSubmissions')}
              title={t('list.openSubmissions')}
            />
          ) : null}
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => goEdit(r.id)}
            aria-label={t('list.editForm')}
            title={t('list.editForm')}
          />
          <Popconfirm
            title={t('list.delete.title')}
            description={t('list.delete.confirm', { title: r.title })}
            okText={t('common:actions.delete')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => deleteMutation.mutate(r.id)}
            disabled={!canManage}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={15} />}
              disabled={!canManage}
              aria-label={t('common:actions.delete')}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div data-testid="forms-page">
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('title') }]}
        actions={
          canManage ? (
            <Button type="primary" icon={<Plus size={16} />} onClick={() => setModalOpen(true)} data-testid="form-new">
              {t('new')}
            </Button>
          ) : null
        }
      />

      <DataTable<FormListItem>
        data={forms}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        emptyState={
          <div style={{ textAlign: 'center', padding: 32 }}>
            <FileText size={28} style={{ color: 'var(--ds-text-tertiary)' }} />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{t('empty')}</Text>
            </div>
          </div>
        }
        filters={
          <Space wrap>
            <Segmented<TypeFilter>
              size="small"
              value={formType}
              onChange={setFormType}
              options={[
                { value: 'ALL', label: t('common:labels.all') },
                ...FORM_TYPES.map((type) => ({ value: type, label: td(`type.${type}`) })),
              ]}
            />
            <LanguagePicker
              value={languageId}
              onChange={setLanguageId}
              allowClear
              placeholder={t('common:contentLanguage.filterAll')}
              style={{ minWidth: 180 }}
            />
          </Space>
        }
      />

      <FormCreateModal
        open={modalOpen}
        saving={createMutation.isPending}
        onCancel={() => setModalOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
      />
    </div>
  );
}
